import {useMemo, useState} from 'react';
import {Link as RouterLink, useNavigate, useParams} from 'react-router-dom';
import {Box, Button, Link, Stack} from '@mui/material';
import {MRT_ColumnDef, MRT_SortingState} from 'material-react-table';
import {useSnackbar} from 'notistack';

import {
  ExternalSubCategoryBindControl,
  SubCategory,
  useGetBoundExternalSubCategories,
  useGetSubCategories,
  useUnbindExternalSubCategory,
} from '@entities/category';
import {Topic, useGetTopics} from '@entities/topic';
import {CardBox, MrTable, TableRowActionsContainer} from '@shared/components';
import {FilterRow, SearchFilter} from '@shared/components/filters';
import {
  DATETIME_FORMAT,
  entityTableCommonProps,
  ROUTE__TOPIC_DETAILS,
} from '@shared/constants';
import {formatInTz} from '@shared/utils';
import {BaseWidgetLayout, Header} from '@widgets/common';
import {EditSubCategoryForm} from './EditSubCategoryForm';

import {COLOR__WHITE} from '@/theme/colors';

const topicColumns: MRT_ColumnDef<Topic>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    size: 300,
    enableSorting: false,
    Cell: ({row}) => {
      const {topicId, logoUrl, name} = row.original;
      const displayName = name || topicId;
      return (
        <Box display="flex" alignItems="center" gap={1}>
          {logoUrl && (
            <img
              src={logoUrl}
              width={28}
              height={28}
              alt={displayName}
              style={{borderRadius: 6, objectFit: 'cover', flexShrink: 0}}
            />
          )}
          <Link
            component={RouterLink}
            to={ROUTE__TOPIC_DETAILS.replace(':topic_id', topicId)}
          >
            {displayName}
          </Link>
        </Box>
      );
    },
  },
  {
    accessorKey: 'publishedEventsCount',
    header: 'Published events',
    size: 160,
    enableSorting: false,
    Cell: ({row}) => row.original.publishedEventsCount ?? 0,
  },
  {
    accessorKey: 'startDate',
    header: 'Start date',
    size: 160,
    enableSorting: false,
    Cell: ({row}) => {
      const date = row.original.startDate;
      if (!date) return null;
      return formatInTz(date, DATETIME_FORMAT);
    },
  },
];

type FiltersState = {
  pageIndex: number;
  pageSize: number;
  searchQuery?: string;
};

interface ExternalSubCategoriesCardProps {
  categorySlug: string;
  subCategoryId: string;
}

const ExternalSubCategoriesCard = ({
  categorySlug,
  subCategoryId,
}: ExternalSubCategoriesCardProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {data: bound, isPending: isBoundPending} =
    useGetBoundExternalSubCategories(subCategoryId);
  const {mutateAsync: unbind, isPending: isUnbinding} =
    useUnbindExternalSubCategory(subCategoryId);

  const columns = useMemo<MRT_ColumnDef<ExternalSubCategory>[]>(
    () => [
      {accessorKey: 'name', header: 'Name'},
      {accessorKey: 'externalId', header: 'External ID', size: 240},
      {accessorKey: 'categorySlug', header: 'Category', size: 180},
    ],
    [],
  );

  const handleUnbind = async (externalSubCategoryId: string) => {
    try {
      await unbind(externalSubCategoryId);
      enqueueSnackbar('External subcategory unbound successfully', {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('Failed to unbind external subcategory', {
        variant: 'error',
      });
    }
  };

  return (
    <CardBox bgcolor={COLOR__WHITE}>
      <Header
        title="External subcategories"
        mb={2}
        isBackButtonShown={false}
        rightPart={
          <ExternalSubCategoryBindControl
            categorySlug={categorySlug}
            subCategoryId={subCategoryId}
          />
        }
      />

      <MrTable
        columns={columns}
        data={bound ?? []}
        {...entityTableCommonProps}
        enableRowActions
        positionActionsColumn="last"
        renderRowActions={({row}) => (
          <TableRowActionsContainer>
            <Button
              variant="text"
              color="secondary"
              disabled={isUnbinding}
              onClick={() => void handleUnbind(row.original.id)}
            >
              Unbind
            </Button>
          </TableRowActionsContainer>
        )}
        state={{isLoading: isBoundPending}}
      />
    </CardBox>
  );
};

export const EditSubCategoryPage = () => {
  const {categoryId, id} = useParams<{categoryId: string; id: string}>();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<FiltersState>({
    pageIndex: 0,
    pageSize: 10,
    searchQuery: undefined,
  });

  const {data, isPending, error} = useGetSubCategories(categoryId ?? '', {
    enabled: !!categoryId,
  });

  const sorting: MRT_SortingState = [{id: 'createdAt', desc: true}];

  const {data: topicsData, isPending: isTopicsPending} = useGetTopics(
    {
      page: filters.pageIndex + 1,
      pageSize: filters.pageSize,
      searchQuery: filters.searchQuery,
      categoryId: categoryId,
      subCategoryIds: id,
      sortField: 'created_at',
      sortOrder: 'desc',
    },
    {enabled: !!id},
  );

  const handleClose = () => {
    navigate(-1);
  };

  const renderContent = (subCategories: SubCategory[] | undefined) => {
    if (!subCategories) {
      return null;
    }

    const subCategory = subCategories.find(subCat => subCat.id === id);

    if (!subCategory) {
      return 'Invalid sub-category ID';
    }

    return (
      <Stack spacing={3}>
        <CardBox bgcolor={COLOR__WHITE}>
          <EditSubCategoryForm
            categoryId={categoryId!}
            subCategory={subCategory}
            onClose={handleClose}
          />
        </CardBox>

        <MrTable
          columns={topicColumns}
          data={topicsData?.items ?? []}
          renderTopToolbarCustomActions={() => (
            <Box display="flex" alignItems="center" width="100%">
              <FilterRow>
                <SearchFilter
                  value={filters.searchQuery}
                  onChange={searchQuery =>
                    setFilters(prev => ({...prev, pageIndex: 0, searchQuery}))
                  }
                />
              </FilterRow>
            </Box>
          )}
          enablePagination
          manualPagination
          rowCount={topicsData?.total ?? 0}
          onPaginationChange={updater => {
            setFilters(prev => {
              const next =
                typeof updater === 'function'
                  ? updater({
                      pageIndex: prev.pageIndex,
                      pageSize: prev.pageSize,
                    })
                  : updater;
              return {...prev, ...next};
            });
          }}
          state={{
            isLoading: isTopicsPending,
            pagination: {
              pageIndex: filters.pageIndex,
              pageSize: filters.pageSize,
            },
            sorting,
          }}
          {...entityTableCommonProps}
          enableRowActions={false}
        />

        {subCategory.uuid && (
          <ExternalSubCategoriesCard
            categorySlug={categoryId!}
            subCategoryId={subCategory.uuid}
          />
        )}
      </Stack>
    );
  };

  if (!categoryId) {
    return 'Invalid category ID';
  }

  return (
    <>
      <Header title="Subcategory" mb={1} />

      <BaseWidgetLayout isPending={isPending} error={error}>
        {renderContent(data)}
      </BaseWidgetLayout>
    </>
  );
};
